import { ImageKit, Modal } from "@aph/ui-lib-humanx";
import sha256 from "crypto-js/sha256";
import cryptoRandomString from "crypto-random-string";
import { useEffect, useReducer, useState } from "react";
import { add } from "date-fns/add";
import { getOauthAuthCodeAPI, getPreAuthKeyAPI } from "@/helpers/oAuthHelper";
import { generateOtpAPI, resendOtpAPI, verifyLoginOtpAPI } from "@/helpers/SSOHelpers";
import { decryptAuthCode, reEncryptAuthCode } from "@/libs/helpers";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import openAuthModalStateSelector from "@/redux/slice/auth/selectors/authModalState";
import { handleOpenLoginModal, updateSSOToken } from "@/redux/slice/auth/slice";
import { NEXT_PUBLIC_BASE_URL, NEXT_PUBLIC_CDN_URL } from "@/libs/constants/auth";
import OtpInput from "@/components/SignIn/OtpInput";
import PhoneInput from "@/components/SignIn/PhoneInput";
import { SignInState } from "@/components/SignIn/signIninterface";
import { SignInAction } from "@/components/SignIn/types";
import {
  CHECK_DIGIT_REGEX,
  LOCK_TIME_IN_MINUTES,
  MAX_INVALID_ATTEMPTS,
  PHONE_MAX_LENGTH,
} from "@/components/SignIn/constants";
import { selectAllPatients } from "@/redux/slice/auth/selectors/patientDetails";
import { isPhoneNumberValid } from "@/helpers";
import styles from "./SignIn.module.scss";

const initialSignInState: SignInState = {
  phoneNumber: "",
  otp: ["", "", "", "", "", ""],
  isOtpValid: false,
  currentStep: 1,
  isShowCountDown: false,
  invalidOtpCount: 0,
};

const reducer = (state: SignInState, action: SignInAction): SignInState => {
  switch (action.type) {
    case "SET_PHONE":
      return { ...state, phoneNumber: action.payload };
    case "SET_OTP":
      return { ...state, otp: action.payload };
    case "VALIDATE_OTP":
      return { ...state, isOtpValid: action.payload };
    case "RESET":
      return initialSignInState;
    case "SET_CURRENT_STEP":
      return { ...state, currentStep: action.payload };
    case "SET_SHOW_COUNTDOWN":
      return { ...state, isShowCountDown: action.payload };
    case "SET_INVALID_OTP_COUNT":
      return { ...state, invalidOtpCount: action.payload };
    default:
      return state;
  }
};

const SignIn: React.FC<any> = () => {
  const isAuthModalOpen = useAppSelector(openAuthModalStateSelector);
  const [state, setLoginStates] = useReducer(reducer, initialSignInState);
  const dispatch = useAppDispatch();
  const [pkce, setPkce] = useState<any>({});
  const [codeChallenge, setCodeChallenge] = useState<string>("");
  const [customLoginId, setCustomLoginId] = useState<string>("");
  const [isSendingOtp, setIsSendingOtp] = useState<boolean>(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [timer, setTimer] = useState<number>(30);
  const allCurrentPatients = useAppSelector(selectAllPatients);
  const onePrimaryUser = allCurrentPatients?.filter((x) => x?.id).length;

  // Login Dependency Functions - Start

  const generatePkce = async () => {
    const verifier = cryptoRandomString({ length: 50, type: "alphanumeric" });
    const challenge = await new Promise((resolve) => {
      resolve(sha256(`${verifier}`));
    });

    return {
      verifier,
      challenge,
    };
  };

  const fetchPkce = async () => {
    const pkce = await generatePkce();
    setPkce(pkce);
  };

  useEffect(() => {
    if (onePrimaryUser) {
      dispatch(handleOpenLoginModal(false));
      setIsVerifyingOtp(false);
      setLoginStates({ type: "RESET" });
    }
  }, [onePrimaryUser]);

  const handleOauthAuthToken = async (
    response_type: string,
    redirect_uri: string,
    state: string,
    code_challenge: string
  ): Promise<any> => {
    if (!!pkce?.verifier) {
      const response = await getOauthAuthCodeAPI(response_type, redirect_uri, state, code_challenge);

      if (response) {
        const auth_code_url = response?.data;
        const params = new URL(auth_code_url)?.searchParams;
        const urlParams = new URLSearchParams(params);
        const code = urlParams.get("auth_code");

        if (code) {
          const decryptedCode = decryptAuthCode(code);

          if (decryptedCode) {
            const codeChallengeFinal = reEncryptAuthCode(decryptedCode);
            // setRedirectUri(auth_code_url);
            setCodeChallenge(codeChallengeFinal);
          }
        }
      }
    }
  };

  const handlePreAuthToken = async (auth_code: string, code_verifier: string): Promise<any> => {
    if (!!pkce?.verifier) {
      const response = await getPreAuthKeyAPI(auth_code, code_verifier);
      const PreAuthCode = response.data;
      // setPreAuthCode(PreAuthCode);
      !!PreAuthCode && localStorage.setItem("preAuthToken", PreAuthCode);
    }
  };

  useEffect(() => {
    fetchPkce();
  }, []);

  useEffect(() => {
    handleOauthAuthToken(`code`, `${NEXT_PUBLIC_BASE_URL}`, `login`, `${pkce?.challenge}`);
  }, [pkce?.challenge]);

  useEffect(() => {
    handlePreAuthToken(`${codeChallenge}`, `${pkce?.verifier}`);
  }, [codeChallenge]);

  // Login Dependency Functions - End

  const handleMobileNumber = (phone: string) => {
    if (isPhoneNumberValid(phone)) {
      setErrorMessage("");
    }

    setLoginStates({ type: "SET_PHONE", payload: phone });
  };

  const handleOtpValue = (otp: string[]) => {
    setLoginStates({ type: "SET_OTP", payload: otp });
  };

  const handlePhoneSubmit = async (phone: string) => {
    // Basic validation to ensure the phone number is 10 digits
    if (isPhoneNumberValid(phone)) {
      try {
        setIsSendingOtp(true);
        const otpApiResponse = await generateOtpAPI(`+91${phone}`);

        if (otpApiResponse?.response?.success) {
          setCustomLoginId(otpApiResponse?.response?.loginId || "");
          setIsSendingOtp(false);
          setErrorMessage("");
          localStorage.setItem("mobile_num", `+91${phone}`);
          setLoginStates({ type: "SET_SHOW_COUNTDOWN", payload: true });
          setLoginStates({ type: "SET_CURRENT_STEP", payload: 2 });
        }
      } catch (error) {
        setErrorMessage("Server Error. Please try again later.");
        setIsSendingOtp(false);
      }
    } else {
      setErrorMessage("Please enter a valid 10-digit phone number.");
    }
  };

  const handleResendOtp = async () => {
    try {
      setIsSendingOtp(true);
      const resendOtpApiResponse = await resendOtpAPI(state.phoneNumber, customLoginId);

      if (resendOtpApiResponse?.response?.success) {
        setCustomLoginId(resendOtpApiResponse?.response?.loginId || "");
        setIsSendingOtp(false);
        setLoginStates({ type: "SET_SHOW_COUNTDOWN", payload: true });
      }
    } catch (error) {
      setErrorMessage("Server Error. Please try again later.");
      setIsSendingOtp(false);
    }
  };

  const handleBackToPhoneScreen = () => {
    setLoginStates({ type: "SET_OTP", payload: initialSignInState?.otp });
    setLoginStates({ type: "SET_CURRENT_STEP", payload: 1 });
    setErrorMessage("");
  };

  const handleOtpSubmit = async (otp: string) => {
    try {
      setIsVerifyingOtp(true);
      const res = await verifyLoginOtpAPI(otp, customLoginId);

      if (res?.response?.authToken) {
        setLoginStates({ type: "VALIDATE_OTP", payload: true });
        dispatch(updateSSOToken(res?.response?.authToken));
      } else {
        const invalidAttempts: number = res?.response?.incorrectAttempts || 0;

        if (invalidAttempts === MAX_INVALID_ATTEMPTS) {
          const timeoutPayload = {
            phoneNumber: state.phoneNumber,
            timeout: add(new Date(), { minutes: LOCK_TIME_IN_MINUTES }),
          };
          localStorage.setItem("otpTimeout", JSON.stringify(timeoutPayload));
        }

        setLoginStates({ type: "VALIDATE_OTP", payload: false });
        setLoginStates({ type: "SET_INVALID_OTP_COUNT", payload: invalidAttempts });
        setLoginStates({ type: "SET_OTP", payload: Array(6).fill("") });
        setErrorMessage(`Incorrect OTP, ${MAX_INVALID_ATTEMPTS - invalidAttempts} attempts left.`);
        setIsVerifyingOtp(false);
      }
    } catch (error) {
      setErrorMessage("Server Error. Please try again later.");
      setIsVerifyingOtp(false);
    }
  };

  // Timer logic
  useEffect(() => {
    let countdown;

    if (state.isShowCountDown) {
      countdown = setInterval(() => {
        setTimer((prev) => {
          if (prev === 1) {
            clearInterval(countdown);
            setLoginStates({ type: "SET_SHOW_COUNTDOWN", payload: false });

            return 30;
          }

          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(countdown); // Cleanup interval
  }, [state.isShowCountDown]);

  const handleModalClose = () => {
    setLoginStates({ type: "RESET" });
    setErrorMessage("");
    dispatch(handleOpenLoginModal(false));
  };

  return (
    <Modal
      open={isAuthModalOpen}
      size="sm"
      handleOpen={() => handleModalClose()}
      className={styles.signInWrapper}
      titleClass={styles.titleClass}
      modalContentClass={styles.circleModalContent}
      mobileBottomAlign
      modalboxClass={styles.modalboxClass}
    >
      <div className={styles.root}>
        <section className={styles.sectionTop}>
          <div className={styles.content}>
            <div className={styles.topLeft}>
              <h1 className={styles.heading}>Momverse</h1>
              <div className={styles.topText}>
                <strong>Login</strong> for a personalised experience
              </div>
            </div>
            <div className={styles.topRight}>
              <ImageKit
                src={`${NEXT_PUBLIC_CDN_URL}/images/momverse/momverse-login-2.png`}
                alt="login modal image"
                width={360}
                height={150}
              />
            </div>
          </div>
        </section>

        <section className={styles.loginInputs}>
          <form id="loginForm" role="form" method="post">
            {state.currentStep === 1 ? (
              <PhoneInput
                name="mobileNumber"
                isSendingOtp={isSendingOtp}
                handlePhoneSubmit={handlePhoneSubmit}
                handleMobileNumber={handleMobileNumber}
                mobileNumber={state.phoneNumber}
                errorMessage={errorMessage}
              />
            ) : (
              <OtpInput
                handleOtpSubmit={handleOtpSubmit}
                handleOtpValue={handleOtpValue}
                otp={state.otp}
                isVerifyingOtp={isVerifyingOtp}
                phoneNumber={state.phoneNumber}
                errorMessage={errorMessage}
                handleResendOtp={handleResendOtp}
                isSendingOtp={isSendingOtp}
                handleBackToPhoneScreen={handleBackToPhoneScreen}
                timer={timer}
                isShowCountDown={state.isShowCountDown}
                invalidOtpCount={state.invalidOtpCount}
              />
            )}
          </form>
        </section>

        <section className={styles.bannerSection}>
          <div className={styles.banner}>
            <ImageKit
              src={`${NEXT_PUBLIC_CDN_URL}/images/momverse/momverse-login-1.png`}
              alt="login banner"
              width={360}
              height={150}
            />
          </div>
        </section>

        <section className={styles.offerStrip}>
          <div className={styles.strip}>Exciting offers for first-time users</div>
        </section>
      </div>
    </Modal>
  );
};

export default SignIn;
